'use client';

import { useState } from 'react';
import Image from 'next/image';
import { Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import { videoThumbnailUrl, videoProviderName } from '@/lib/video-embed';

/**
 * A gallery video item, shown as a still.
 *
 * Used everywhere a video appears as something to click rather than something
 * to watch: the thumbnail strip under the product gallery, and the media tiles
 * in the create and edit forms. One component because the three of them have to
 * agree on what a video looks like, and because the fallback below is easy to
 * forget in a copy.
 *
 * THE POSTER IS OPTIONAL AND ITS ABSENCE IS NOT AN ERROR STATE. YouTube derives
 * one from the video id; Vimeo does not publish one without an API call; and a
 * video that has been deleted or made private still has a poster URL that
 * simply 404s. So the glyph and the provider's name are the real rendering and
 * the picture is an upgrade on top of it.
 *
 * The first version of this drew the glyph underneath and painted the poster
 * over it, on the theory that a failed poster would reveal the glyph. It does
 * not: a broken <img> still paints the browser's own broken-image icon in the
 * corner, so a video whose poster did not load rendered as a broken graphic
 * over a play button, which reads as a bug in the page rather than a video with
 * no picture. The poster is now removed from the tree the moment it fails, so
 * what is left is the deliberate fallback.
 */
export function VideoThumbnail({
  src,
  /** Larger badge and a visible provider label. For tiles bigger than the strip. */
  size = 'sm',
  className,
}: {
  src: string;
  size?: 'sm' | 'md';
  className?: string;
}) {
  // The src that failed, rather than a bare boolean: a tile is reused as the
  // seller reorders the gallery, and a boolean would keep a working poster
  // hidden because a DIFFERENT video's poster had failed in the same slot.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const poster = failedSrc === src ? null : videoThumbnailUrl(src);
  const provider = videoProviderName(src);

  return (
    <div className={cn('absolute inset-0 bg-muted', className)}>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-muted-foreground">
        <Play className={size === 'md' ? 'h-6 w-6' : 'h-5 w-5'} aria-hidden />
        {size === 'md' && (
          <span className="text-[10px] px-1 text-center leading-tight">
            {provider ? `${provider} video` : 'Video'}
          </span>
        )}
      </div>

      {poster && (
        <>
          <Image
            src={poster}
            alt=""
            fill
            // Poster frames are 320x180 and every box this renders into is
            // small, so one size covers all of them.
            sizes="200px"
            // COVER, not contain: the poster is 16:9 and these boxes are
            // square, so a contained poster would sit in a letterboxed band
            // inside a strip whose other items fill their squares.
            className="object-cover"
            // The poster is already a small same-origin file from
            // /api/image-proxy (see videoThumbnailUrl), so there is nothing for
            // the optimizer to do (it is on in production since 2026-10-21, and
            // would only fetch the proxy's file back through itself).
            unoptimized
            // Anything that stops the file arriving: a deleted video, a private
            // one, YouTube being unreachable, or the proxy refusing the request
            // or rate limiting it. All of them land here and all of them mean
            // the same thing to a viewer.
            onError={() => setFailedSrc(src)}
          />
          {/* A play badge over the picture. Without it a video item is
              indistinguishable from a screenshot until it is clicked, and a
              bare white triangle disappears into a bright poster. */}
          <span className="absolute inset-0 flex items-center justify-center text-white">
            <span
              className={cn(
                'flex items-center justify-center rounded-full bg-black/60',
                size === 'md' ? 'h-9 w-9' : 'h-7 w-7',
              )}
            >
              <Play className={size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5'} aria-hidden fill="currentColor" />
            </span>
          </span>
        </>
      )}
    </div>
  );
}
